import React, { useState } from 'react';
import { Box, Typography, Button, Modal, CircularProgress } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import '../../Styles/AssetVarianceTable.css';

const AutoRebalanceWiseModal = ({
    open,
    onClose,
    onConfirm,
    strategyName,
    filteredVarianceData,
    varianceData,
    orders,
    isRebalancing,
    currentStep = 2,
    onStepBack
}) => {
    // State to track selected wash-sale trades
    const [selectedWashTrades, setSelectedWashTrades] = useState(new Set());

    // Format numbers to prevent overflow and improve readability
    const formatNumber = (value, divisor) => {
        if (!value || !divisor || divisor === 0) return '0.000';
        const result = Number(value) / Number(divisor);
        
        // For very large numbers, use scientific notation with reduced precision
        if (Math.abs(result) >= 1e6 || Math.abs(result) < 1e-3) {
            // Convert to exponential and limit digits before decimal
            const expStr = result.toExponential(3);
            const [mantissa, exponent] = expStr.split('e');
            const mantissaNum = parseFloat(mantissa);
            
            // Limit mantissa to reasonable length (max 6 digits before decimal)
            if (Math.abs(mantissaNum) >= 1e6) {
                return mantissaNum.toExponential(2) + 'e' + exponent;
            }
            return expStr;
        }
        return result.toFixed(3);
    };

    // Handle checkbox change
    const handleWashTradeSelection = (tradeKey) => {
        setSelectedWashTrades(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tradeKey)) {
                newSet.delete(tradeKey);
            } else {
                newSet.add(tradeKey);
            }
            return newSet;
        });
    };



    // Get selected wash-sale trades data
    const getSelectedWashTrades = () => {
        const selectedTrades = [];
        if (Array.isArray(varianceData) && varianceData.length > 0) {
            varianceData.forEach((user) => {
                if (Array.isArray(user.assetsVariance) && user.assetsVariance.length > 0) {
                    user.assetsVariance.forEach((asset) => {
                        if (asset.isWashTrade === true) {
                            const tradeKey = `${user.userRole?.id}-${asset.asset?.id}`;
                            if (selectedWashTrades.has(tradeKey)) {
                                selectedTrades.push({
                                    user: user,
                                    asset: asset
                                });
                            }
                        }
                    });
                }
            });
        }
        return selectedTrades;
    };

    // Handle confirm with selected wash-sale trades
    const handleConfirm = () => {
        const selectedTrades = getSelectedWashTrades();
        onConfirm(selectedTrades);
    };

    // Determine which price column to show based on order type
    const isLimitOrder = Array.isArray(orders) && orders.length > 0 && orders.some(o => (o.orderType || '').toUpperCase() === 'LMT');
    const showMarketPrice = !isLimitOrder;
    const showLimitPrice = isLimitOrder;
    const baseColumnCount = 6; // Investor, Asset, Current Amount, New Amount, Variance ($), Buy/Sell
    const totalColumns = baseColumnCount + (showMarketPrice ? 1 : 0) + (showLimitPrice ? 1 : 0);

    // Ensure table and cells inherit the same font as confirmation modal
    const tableStylesFont = { fontFamily: 'inherit', fontSize: '16px' };
    const tableCellStylesFont = { fontFamily: 'inherit' };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '95vw',
                maxWidth: '1400px',
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                padding: '20px 24px',
                outline: 'none',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span className="rebalancing-title">Rebalancing</span>
                        <span className="rebalancing-strategy-name">{strategyName}</span>
                    </Typography>
                    <Button
                        onClick={onStepBack || onClose}
                        sx={{
                            padding: "8px",
                            minWidth: "auto",
                            color: "#666",
                            fontSize: "24px",
                            fontWeight: "600",
                            lineHeight: "32px",
                            fontFamily: "Roboto",
                            fontStyle: "normal",
                            textAlign: "center",
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#333',
                            }
                        }}
                    >
                        ×
                    </Button>
                </Box>
                {/* Content */}
                <Box sx={{ display: 'flex', gap: 4, flex: 1, overflow: 'hidden', minHeight: 0 }}>
                    {/* Left: Preview your trades */}
                    <Box className="preview-trades-left-panel" sx={{ flex: 1, maxHeight: '100%', overflowY: 'auto', minWidth: 0 }}>
                        <Box className="preview-your-trades-box">
                            <Typography className="preview-your-trades-title" variant="h6">
                                Preview your trades
                            </Typography>
                            <Typography className="preview-your-trades-subtitle">
                                Preview all trades under your order:
                            </Typography>
                        </Box>

                        {/* Attention Banner */}
                        <Box className="attention-banner">
                            <ErrorOutlineIcon className="attention-icon" />
                            <Box className="attention-content">
                                <Typography className="attention-title">
                                    Attention!
                                </Typography>
                                <Box className="attention-content-box">
                                    <Typography className="attention-subtitle">
                                        These prices are valid for 10 seconds. Confirm your investment to buy at these prices.
                                    </Typography>
                                    <Typography className="attention-subtitle">
                                        Time Remaining: 10 Seconds
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{
                            overflowY: 'auto',
                            maxHeight: '400px',
                            maxWidth: '100%'
                        }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'separate',
                                borderSpacing: 0,
                                tableLayout: 'fixed',
                                minWidth: '1000px',
                                ...tableStylesFont
                            }}>
                                <colgroup>
                                    <col style={{ width: '12%' }} /> {/* Investor */}
                                    <col style={{ width: '8%' }} />  {/* Asset */}
                                    {showMarketPrice && <col style={{ width: '10%' }} />} {/* Market Price */}
                                    {showLimitPrice && <col style={{ width: '10%' }} />} {/* Limit Price */}
                                    <col style={{ width: '12%' }} /> {/* Current Amount */}
                                    <col style={{ width: '10%' }} /> {/* New Amount */}
                                    <col style={{ width: '10%' }} /> {/* Variance ($) */}
                                    <col style={{ width: '8%' }} />  {/* Buy/Sell */}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="preview-table-header" style={tableCellStylesFont}>Investor</th>
                                        <th className="preview-table-header" style={tableCellStylesFont}>Asset</th>
                                        {showMarketPrice && (
                                            <th className="preview-table-header" style={tableCellStylesFont}>Market Price</th>
                                        )}
                                        {showLimitPrice && (
                                            <th className="preview-table-header" style={tableCellStylesFont}>Limit Price</th>
                                        )}
                                        <th className="preview-table-header" style={tableCellStylesFont}>Current Amount</th>
                                        <th className="preview-table-header" style={tableCellStylesFont}>New Amount</th>
                                        <th className="preview-table-header" style={tableCellStylesFont}>Variance ($)</th>
                                        <th className="preview-table-header" style={tableCellStylesFont}>Buy/Sell</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(filteredVarianceData) && filteredVarianceData.length > 0 ? (
                                        (() => {
                                            // Only show assets that are part of the current order list
                                            const selectedSymbols = new Set(
                                                Array.isArray(orders)
                                                    ? orders
                                                        .map(o => o?.asset)
                                                        .filter(sym => typeof sym === 'string' && sym.trim() !== '')
                                                    : []
                                            );

                                            return filteredVarianceData.map((user, idx) => {
                                                const assetsForUser = Array.isArray(user.assetsVariance)
                                                    ? user.assetsVariance.filter(a => selectedSymbols.has(a?.asset?.symbol))
                                                    : [];

                                                return assetsForUser.map((asset, aidx) => {
                                                    const buySell = Number(asset.idealPortfolio) - Number(asset.currentPortfolio);
                                                    const marketPrice = Number(asset.price) || 0;
                                                    const limitPrice = Number(orders.find(o => o.asset === asset.asset?.symbol)?.limitPrice) || marketPrice;
                                                    
                                                    const currentAmount = formatNumber(asset.currentPortfolio, marketPrice);
                                                    const newAmount = formatNumber(asset.idealPortfolio, marketPrice);
                                                    const variance = Math.abs(buySell);
                                                    const formattedVariance = formatNumber(variance, 1); // Use 1 as divisor since variance is already in dollars

                                                    // Use unique user ID instead of firstName to avoid conflicts with duplicate names
                                                    const uniqueUserId = user.userRole?.id || `user-${idx}`;
                                                    const uniqueAssetId = asset.asset?.id || asset.asset?.name || `asset-${aidx}`;
                                                    return (
                                                        <tr key={`${uniqueUserId}-${uniqueAssetId}`} className="preview-table-row">
                                                            {aidx === 0 && (
                                                                <td rowSpan={assetsForUser.length} className="preview-table-cell preview-table-cell-investor" style={tableCellStylesFont}>
                                                                    {user.userRole?.user?.userInfo?.firstName || '-'}
                                                                </td>
                                                            )}
                                                            <td className="preview-table-cell" style={tableCellStylesFont}>
                                                                {asset.asset?.symbol || '-'}
                                                            </td>
                                                            {showMarketPrice && (
                                                                <td className="preview-table-cell" style={tableCellStylesFont}>
                                                                    ${marketPrice.toFixed(2)}
                                                                </td>
                                                            )}
                                                            {showLimitPrice && (
                                                                <td className="preview-table-cell" style={tableCellStylesFont}>
                                                                    ${Number(limitPrice).toFixed(2)}
                                                                </td>
                                                            )}
                                                            <td className="preview-table-cell" style={{
                                                                ...tableCellStylesFont,
                                                                whiteSpace: 'nowrap',
                                                                minWidth: '150px'
                                                            }}>
                                                                {currentAmount}
                                                            </td>
                                                            <td className="preview-table-cell" style={{
                                                                ...tableCellStylesFont,
                                                                whiteSpace: 'nowrap',
                                                                minWidth: '150px'
                                                            }}>
                                                                {newAmount}
                                                            </td>
                                                            <td className="preview-table-cell" style={{
                                                                ...tableCellStylesFont,
                                                                whiteSpace: 'nowrap',
                                                                minWidth: '150px'
                                                            }}>
                                                                ${formattedVariance}
                                                            </td>
                                                            <td className={`preview-table-cell ${buySell < 0 ? 'preview-table-cell-sell' : 'preview-table-cell-buy'}`} style={tableCellStylesFont}>
                                                                {buySell < 0 ? 'Sell' : 'Buy'}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            });
                                        })()
                                    ) : (
                                        <tr><td colSpan={totalColumns} style={{ textAlign: 'center', padding: 20, fontSize: '14px' }}>No data available</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </Box>
                    </Box>

                    {/* Right: Wash Sale Warning and Table */}
                    <Box className="wash-sale-right-panel" sx={{ flex: 1, minWidth: 0, overflowY: 'auto', maxHeight: '100%' }}>
                        <Box className="wash-sale-warning-content-box">
                            <Typography sx={{ fontWeight: 600, color: '#000', mb: 1 }}>
                                Beware! Risk of Wash-Sale Trade
                            </Typography>
                            <Box className="wash-sale-warning-content-box-inner">
                                <Typography className="wash-sale-warning-subtitle">
                                    The following trades under your order meet conditions for "Wash Sale" trade, and may get flagged by the SEC.
                                </Typography>
                                <Typography className="wash-sale-warning-description">
                                    Select the trades you'd like to proceed with anyway.
                                </Typography>
                            </Box>
                        </Box>
                        <Box className="wash-sale-table-container">
                            <table className="wash-sale-table" style={tableCellStylesFont}>
                                <thead>
                                    <tr>
                                        <th className="wash-sale-table-header" style={tableCellStylesFont}>Investor</th>
                                        <th className="wash-sale-table-header" style={tableCellStylesFont}>Asset</th>
                                        <th className="wash-sale-table-header" style={tableCellStylesFont}>Price</th>
                                        <th className="wash-sale-table-header" style={tableCellStylesFont}>Quantity</th>
                                        <th className="wash-sale-table-header" style={tableCellStylesFont}>Value($)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // Collect all wash trade assets from all users
                                        const washTradeData = [];

                                        if (Array.isArray(varianceData) && varianceData.length > 0) {
                                            varianceData.forEach((user) => {
                                                if (Array.isArray(user.assetsVariance) && user.assetsVariance.length > 0) {
                                                    const userWashTrades = [];
                                                    user.assetsVariance.forEach((asset) => {
                                                        // Check if this asset is a wash trade
                                                        if (asset.isWashTrade === true) {
                                                            userWashTrades.push({
                                                                user: user,
                                                                asset: asset
                                                            });
                                                        }
                                                    });
                                                    if (userWashTrades.length > 0) {
                                                        washTradeData.push({
                                                            user: user,
                                                            trades: userWashTrades
                                                        });
                                                    }
                                                }
                                            });
                                        }

                                        if (washTradeData.length > 0) {
                                            return washTradeData.map((userData, userIdx) => {
                                                const { user, trades } = userData;
                                                return trades.map((item, tradeIdx) => {
                                                    const { asset } = item;
                                                    const buySell = Number(asset.idealPortfolio) - Number(asset.currentPortfolio);
                                                    const marketPrice = Number(asset.price) || 0;
                                                    const quantity = asset.idealPortfolio ? (Number(asset.idealPortfolio) / marketPrice).toFixed(3) : '0.000';
                                                    const tradeKey = `${user.userRole?.id}-${asset.asset?.id}-${tradeIdx}`;

                                                    return (
                                                        <tr key={tradeKey} className="wash-sale-table-row">
                                                            {tradeIdx === 0 && (
                                                                <td rowSpan={trades.length} className="wash-sale-table-cell-investor" style={tableCellStylesFont}>
                                                                    {user.userRole?.user?.userInfo?.firstName || '-'}
                                                                </td>
                                                            )}
                                                            <td className="wash-sale-table-cell-asset" style={tableCellStylesFont}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedWashTrades.has(tradeKey)}
                                                                        onChange={() => handleWashTradeSelection(tradeKey)}
                                                                    />
                                                                    <span>{asset.asset?.symbol || '-'}</span>
                                                                </Box>
                                                            </td>
                                                            <td className="wash-sale-table-cell-price" style={tableCellStylesFont}>
                                                                ${marketPrice.toFixed(2)}
                                                            </td>
                                                            <td className="wash-sale-table-cell-quantity" style={tableCellStylesFont}>
                                                                {quantity}
                                                            </td>
                                                            <td className="wash-sale-table-cell-value" style={tableCellStylesFont}>
                                                                ${Math.abs(buySell).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            });
                                        } else {
                                            return (
                                                <tr>
                                                    <td colSpan={5} className="wash-sale-table-no-data">
                                                        No wash-sale trades
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    })()}
                                </tbody>
                            </table>
                        </Box>
                    </Box>
                </Box>
                {/* Footer */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    backgroundColor: '#fff',
                }}>
                    <Button onClick={onStepBack || onClose} variant="outlined" className='confirmation-modal-cancel-button' sx={{ textTransform: 'none', px: 3 }} disabled={isRebalancing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        className='confirmation-modal-continue-button'
                        sx={{ textTransform: 'none', px: 3, backgroundColor: '#1976d2' }}
                        disabled={isRebalancing}
                        startIcon={isRebalancing ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isRebalancing ? 'Processing...' : 'Confirm'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AutoRebalanceWiseModal;
